import type { MessageKey } from './message-key';

// DC08 — display-label lookup for the canonical enumerations
// (docs/design-gap/round2-entity-reconciliation.md appendix, "DR-30 canonical
// enumerations"). Labels only: every left-hand value is a STORED spelling (or a
// named derived display state); no surface ever renders a design short key
// directly — it maps through design-alias.ts first.
//
// Where an existing key already labels a stored value it is reused (e.g. the ten
// `booking.status.*` keys, DB-17); everything else is a `vocab.<enum>.<value>` key
// in the "DC08 canonical status labels" block of en.ts / bn.ts.
export const STATUS_LABEL_KEYS = {
  // bookings.status (DB-17, unchanged) + derived `running` (never stored).
  booking_status: {
    pending: 'booking.status.pending',
    payment_verification: 'booking.status.payment_verification',
    confirmed: 'booking.status.confirmed',
    in_progress: 'booking.status.in_progress',
    completed: 'booking.status.completed',
    cancelled: 'booking.status.cancelled',
    no_show: 'booking.status.no_show',
    rejected: 'booking.status.rejected',
    expired: 'booking.status.expired',
    refunded: 'booking.status.refunded',
    running: 'vocab.booking_status.running',
  },
  // payment_proofs.status.
  proof_status: {
    submitted: 'user.detail.proof_status.submitted',
    verified: 'user.detail.proof_status.verified',
    rejected: 'user.detail.proof_status.rejected',
    superseded: 'user.detail.proof_status.superseded',
  },
  // payment_proofs.reject_reason_code (⁺).
  proof_reject_reason: {
    not_received: 'vocab.proof_reject_reason.not_received',
    trx_mismatch: 'vocab.proof_reject_reason.trx_mismatch',
    amount_short: 'vocab.proof_reject_reason.amount_short',
    other: 'vocab.proof_reject_reason.other',
  },
  // offers.status — union of today's 4 and the recommended moderation values,
  // plus derived `scheduled` (active ∧ starts_at > now). "Ended" = `expired`.
  offer_status: {
    draft: 'vocab.offer_status.draft',
    pending_review: 'vocab.offer_status.pending_review',
    active: 'merchant.offers.status.active',
    paused: 'merchant.offers.status.paused',
    expired: 'merchant.offers.status.expired',
    rejected: 'vocab.offer_status.rejected',
    taken_down: 'merchant.offers.status.taken_down',
    scheduled: 'vocab.offer_status.scheduled',
  },
  // reviews.status (⁺, replaces `hidden`) + derived `flagged` (open review_flags row).
  review_status: {
    visible: 'vocab.review_status.visible',
    hidden: 'vocab.review_status.hidden',
    removed: 'vocab.review_status.removed',
    flagged: 'vocab.review_status.flagged',
  },
  // disputes.kind.
  dispute_kind: {
    paid_but_rejected: 'admin.disputes.kind.paid_but_rejected',
    complaint: 'admin.disputes.kind.complaint',
    refund_claim: 'admin.disputes.kind.refund_claim',
    no_service: 'vocab.dispute_kind.no_service',
    no_show: 'vocab.dispute_kind.no_show',
    other: 'admin.disputes.kind.other',
  },
  // disputes.status.
  dispute_status: {
    open: 'admin.status.open',
    escalated: 'vocab.dispute_status.escalated',
    resolved: 'admin.status.resolved',
  },
  // disputes.resolution.
  dispute_resolution: {
    override_confirmed: 'admin.disputes.res.override_confirmed',
    refund_recorded: 'admin.disputes.res.refund_recorded',
    uphold_merchant: 'vocab.dispute_resolution.uphold_merchant',
    warn_customer: 'vocab.dispute_resolution.warn_customer',
    penalty_merchant: 'vocab.dispute_resolution.penalty_merchant',
    block_customer: 'vocab.dispute_resolution.block_customer',
    dismissed: 'admin.disputes.res.dismissed',
  },
  // businesses.status (online_paused is a flag, never a status — M41).
  business_status: {
    draft: 'merchant.biz.status.draft',
    pending_approval: 'merchant.biz.status.pending_approval',
    active: 'merchant.biz.status.active',
    suspended: 'merchant.biz.status.suspended',
    banned: 'merchant.biz.status.banned',
    rejected: 'merchant.biz.status.rejected',
    departed: 'merchant.biz.status.departed',
  },
  // Public claim state (derived, DB-46 / FR-B17).
  claim_state: {
    unclaimed: 'claim.unclaimed_badge',
    claim_under_review: 'claim.under_review_badge',
    claimed: 'claim.claimed_badge',
    claimed_verified: 'claim.verified_badge',
  },
  // business_claims.status.
  claim_status: {
    pending: 'admin.claims.status.pending',
    approved: 'admin.claims.status.approved',
    rejected: 'admin.claims.status.rejected',
    withdrawn: 'admin.claims.status.withdrawn',
  },
  // merchant_applications.status.
  application_status: {
    pending_approval: 'admin.status.pending_approval',
    info_requested: 'admin.status.info_requested',
    approved: 'admin.status.approved',
    rejected: 'admin.status.rejected',
  },
  // businesses.payment_kyc_state (⁺).
  payment_kyc_state: {
    not_started: 'vocab.payment_kyc_state.not_started',
    under_review: 'vocab.payment_kyc_state.under_review',
    approved: 'vocab.payment_kyc_state.approved',
    more_info: 'vocab.payment_kyc_state.more_info',
    rejected: 'vocab.payment_kyc_state.rejected',
    blocked_individual: 'vocab.payment_kyc_state.blocked_individual',
  },
  // catalog_items.status (⁺, replaces `active` bool).
  catalog_item_status: {
    draft: 'vocab.catalog_item_status.draft',
    pending_review: 'vocab.catalog_item_status.pending_review',
    changes_requested: 'vocab.catalog_item_status.changes_requested',
    active: 'vocab.catalog_item_status.active',
    rejected: 'vocab.catalog_item_status.rejected',
    paused: 'vocab.catalog_item_status.paused',
    suspended: 'vocab.catalog_item_status.suspended',
    archived: 'vocab.catalog_item_status.archived',
  },
  // catalog_items.visibility (⁺).
  catalog_visibility: {
    marketplace: 'vocab.catalog_visibility.marketplace',
    private: 'vocab.catalog_visibility.private',
  },
  // plans.id (⁺, supersedes DR-26 names).
  plan_tier: {
    free: 'vocab.plan_tier.free',
    premium: 'vocab.plan_tier.premium',
    business_plus: 'vocab.plan_tier.business_plus',
    corporate: 'vocab.plan_tier.corporate',
  },
  // plan_prices.price_kind (⁺).
  price_kind: {
    regular: 'vocab.price_kind.regular',
    launch: 'vocab.price_kind.launch',
    loyalty: 'vocab.price_kind.loyalty',
  },
  // subscriptions.status (`trial` kept in the CHECK, no longer entered).
  subscription_status: {
    trial: 'merchant.sub.status.trial',
    active: 'merchant.sub.status.active',
    grace_period: 'merchant.sub.status.grace_period',
    expired: 'merchant.sub.status.expired',
    cancelled: 'merchant.sub.status.cancelled',
  },
  // subscription_payments.status.
  subscription_payment_status: {
    pending: 'vocab.subscription_payment_status.pending',
    verified: 'vocab.subscription_payment_status.verified',
    rejected: 'vocab.subscription_payment_status.rejected',
  },
  // invoices.status (+ `refunded` ⁺).
  invoice_status: {
    paid: 'admin.status.paid',
    due: 'admin.status.due',
    overdue: 'admin.status.overdue',
    void: 'admin.status.void',
    refunded: 'vocab.invoice_status.refunded',
  },
  // notification_prefs event rows (customer ∪ merchant sets).
  notification_event: {
    booking_confirmed: 'vocab.notification_event.booking_confirmed',
    payment_update: 'vocab.notification_event.payment_update',
    reminder: 'vocab.notification_event.reminder',
    offers: 'vocab.notification_event.offers',
    booking_new: 'vocab.notification_event.booking_new',
    proof_submitted: 'vocab.notification_event.proof_submitted',
    booking_cancelled: 'vocab.notification_event.booking_cancelled',
    review_new: 'vocab.notification_event.review_new',
  },
  // notification_outbox.channel / prefs channels.
  notification_channel: {
    sms: 'vocab.notification_channel.sms',
    email: 'vocab.notification_channel.email',
    push: 'vocab.notification_channel.push',
    inapp: 'vocab.notification_channel.inapp',
    whatsapp: 'vocab.notification_channel.whatsapp',
  },
} as const satisfies Record<string, Record<string, MessageKey>>;

export type StatusEntity = keyof typeof STATUS_LABEL_KEYS;
export type StatusValue<E extends StatusEntity> = keyof (typeof STATUS_LABEL_KEYS)[E] & string;

// Display states computed by the UI and never written to a record.
export const DERIVED_VALUES: { readonly [E in StatusEntity]?: readonly StatusValue<E>[] } = {
  booking_status: ['running'], // in_progress, or confirmed whose window is today
  offer_status: ['scheduled'], // active ∧ starts_at > now
  review_status: ['flagged'], // an open review_flags row
};

// Values labelled here whose adoption still awaits a product decision (GAP
// docs/design-four-module-gap-analysis.md §5 / §7.3 item numbers, recorded by
// DC00). Labelling them is harmless — a surface only meets them once the
// matching migration lands — but they are not stored spellings until then.
export const PENDING_DECISION: {
  readonly [E in StatusEntity]?: {
    readonly decision: string;
    readonly values: readonly StatusValue<E>[];
  };
} = {
  proof_reject_reason: {
    decision: '#27 proof reject reason codes',
    values: ['not_received', 'trx_mismatch', 'amount_short', 'other'],
  },
  offer_status: {
    decision: '#28 offer lifecycle (pre-publish review ⇒ pending_review + rejected)',
    values: ['draft', 'pending_review', 'rejected'],
  },
  review_status: {
    decision: '#19 review states / #12 review requests vs M11',
    values: ['visible', 'hidden', 'removed'],
  },
  dispute_kind: { decision: '#19 dispute kinds', values: ['no_service', 'no_show'] },
  dispute_status: { decision: '#19 dispute states', values: ['escalated'] },
  dispute_resolution: {
    decision: '#19 dispute resolutions',
    values: ['uphold_merchant', 'warn_customer', 'penalty_merchant', 'block_customer'],
  },
  catalog_item_status: {
    decision: '#11 service-level moderation',
    values: [
      'draft',
      'pending_review',
      'changes_requested',
      'active',
      'rejected',
      'paused',
      'suspended',
      'archived',
    ],
  },
  catalog_visibility: { decision: '#11 private URL', values: ['marketplace', 'private'] },
  plan_tier: {
    decision: '#1 plan ladder (supersedes DR-26)',
    values: ['free', 'premium', 'business_plus', 'corporate'],
  },
  price_kind: { decision: '#1 plan ladder', values: ['regular', 'launch', 'loyalty'] },
  invoice_status: { decision: '#1 / #2 billing model', values: ['refunded'] },
  notification_event: {
    decision: '#12 notification matrix',
    values: [
      'booking_confirmed',
      'payment_update',
      'reminder',
      'offers',
      'booking_new',
      'proof_submitted',
      'booking_cancelled',
      'review_new',
    ],
  },
};

export function statusValues<E extends StatusEntity>(entity: E): StatusValue<E>[] {
  return Object.keys(STATUS_LABEL_KEYS[entity]) as StatusValue<E>[];
}

export function isStatusValue<E extends StatusEntity>(
  entity: E,
  value: string,
): value is StatusValue<E> {
  return Object.prototype.hasOwnProperty.call(STATUS_LABEL_KEYS[entity], value);
}

// Typed lookup: the label key for a canonical value.
export function statusLabelKey<E extends StatusEntity>(
  entity: E,
  value: StatusValue<E>,
): MessageKey {
  return (STATUS_LABEL_KEYS[entity] as Record<StatusValue<E>, MessageKey>)[value];
}

// For wire values the client may not know yet (AP-D10: render unknown values via
// fallback). Returns null so the caller can show its own fallback.
export function statusLabelKeyOrNull(entity: StatusEntity, value: string): MessageKey | null {
  return isStatusValue(entity, value) ? statusLabelKey(entity, value) : null;
}
